import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProduct, fetchProducts } from "@/lib/api";
import { formatMXN } from "@/lib/format";
import { useCart } from "@/lib/cartStore";
import { Button } from "@/components/ui/button";
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from "@/components/ui/tabs";
import {
    ChevronRight,
    Minus,
    Plus,
    ShoppingBag,
    Star,
    MapPin,
    Truck,
    Heart,
    Share2,
} from "lucide-react";
import { motion } from "framer-motion";
import ProductCard from "@/components/shared/ProductCard";
import { toast } from "sonner";

export default function ProductPage() {
    const { storeSlug, productSlug } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imageIdx, setImageIdx] = useState(0);
    const [qty, setQty] = useState(1);
    const [variants, setVariants] = useState({});
    const [related, setRelated] = useState([]);
    const cart = useCart();

    useEffect(() => {
        setLoading(true);
        setImageIdx(0);
        setQty(1);
        setVariants({});
        fetchProduct(storeSlug, productSlug)
            .then((d) => {
                setData(d);
                fetchProducts({ store_slug: storeSlug, limit: 8 }).then((all) => {
                    setRelated(all.filter((p) => p.id !== d.product.id));
                });
            })
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [storeSlug, productSlug]);

    // Dynamic page title + description (for Google JS-render and browser tab)
    useEffect(() => {
        if (!data?.product) return;
        const { product, store } = data;
        const baseTitle = "Tiendify";
        document.title = `${product.name} — ${store?.name || baseTitle}`;
        const desc =
            product.shortDescription ||
            (product.description || "").slice(0, 160) ||
            "";
        let m = document.querySelector('meta[name="description"]');
        if (!m) {
            m = document.createElement("meta");
            m.setAttribute("name", "description");
            document.head.appendChild(m);
        }
        m.setAttribute("content", desc);
        return () => {
            document.title = `${baseTitle} — Crea tu tienda en línea, sin renta mensual`;
        };
    }, [data]);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center text-zinc-500 text-sm">
                Cargando producto…
            </div>
        );
    }
    if (!data) {
        return (
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
                <p className="text-lg font-semibold">Producto no encontrado</p>
                <Link
                    to="/"
                    className="mt-4 inline-block text-sm text-indigo-600 hover:underline"
                >
                    Volver al inicio
                </Link>
            </div>
        );
    }

    const { product, store } = data;
    const discount =
        product.originalPrice && product.originalPrice > product.price
            ? Math.round(
                  ((product.originalPrice - product.price) /
                      product.originalPrice) *
                      100
              )
            : null;

    const handleAddToCart = () => {
        const variantStr = product.variants?.length
            ? product.variants
                  .map((v) => variants[v.name])
                  .filter(Boolean)
                  .join(" / ") || null
            : null;
        cart.add(product, qty, variantStr);
        toast.success("Agregado al carrito", {
            description: `${qty} × ${product.name}`,
        });
    };

    const handleBuyNow = () => {
        handleAddToCart();
    };

    return (
        <div data-testid="product-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-1 text-xs text-zinc-500">
                <Link to="/" className="hover:text-zinc-900">
                    Inicio
                </Link>
                <ChevronRight className="w-3 h-3" />
                <Link to={`/${store.slug}`} className="hover:text-zinc-900">
                    {store.name}
                </Link>
                <ChevronRight className="w-3 h-3" />
                <span className="text-zinc-900 truncate">{product.name}</span>
            </nav>

            <div className="mt-8 grid lg:grid-cols-12 gap-8 lg:gap-12">
                {/* Gallery */}
                <div className="lg:col-span-7">
                    <div className="grid grid-cols-12 gap-3">
                        <div className="hidden md:flex md:col-span-2 flex-col gap-3">
                            {product.images.map((img, i) => (
                                <button
                                    key={i}
                                    data-testid={`product-thumb-${i}`}
                                    onClick={() => setImageIdx(i)}
                                    className={`aspect-square rounded-lg overflow-hidden border ${
                                        i === imageIdx
                                            ? "border-indigo-600 ring-2 ring-indigo-100"
                                            : "border-zinc-200"
                                    } transition-all`}
                                >
                                    <img
                                        src={img}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                            ))}
                        </div>
                        <div className="col-span-12 md:col-span-10">
                            <motion.div
                                key={imageIdx}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ duration: 0.25 }}
                                className="aspect-square rounded-2xl overflow-hidden bg-zinc-100 border border-zinc-200"
                            >
                                <img
                                    src={product.images[imageIdx]}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                />
                            </motion.div>
                            <div className="md:hidden mt-3 flex gap-2 overflow-x-auto scrollbar-hide">
                                {product.images.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setImageIdx(i)}
                                        className={`w-16 h-16 rounded-md overflow-hidden border shrink-0 ${
                                            i === imageIdx
                                                ? "border-indigo-600"
                                                : "border-zinc-200"
                                        }`}
                                    >
                                        <img
                                            src={img}
                                            alt=""
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Info */}
                <div className="lg:col-span-5">
                    <div className="flex items-center gap-2">
                        {product.isNew && (
                            <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-zinc-100 text-zinc-700 rounded-full">
                                Nuevo
                            </span>
                        )}
                        {discount && (
                            <span className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-indigo-600 text-white rounded-full">
                                -{discount}%
                            </span>
                        )}
                    </div>
                    <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">
                        {product.name}
                    </h1>
                    <div className="mt-2 flex items-center gap-3 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                            <span className="font-semibold text-zinc-700">
                                {product.rating?.toFixed(1)}
                            </span>{" "}
                            ({product.reviewsCount} reseñas)
                        </span>
                        <span>· {product.stock} en stock</span>
                    </div>

                    <div className="mt-5 flex items-baseline gap-3">
                        <span className="text-3xl font-bold text-zinc-900">
                            {formatMXN(product.price)}
                        </span>
                        {product.originalPrice && (
                            <span className="text-base text-zinc-400 line-through">
                                {formatMXN(product.originalPrice)}
                            </span>
                        )}
                    </div>

                    <p className="mt-5 text-sm text-zinc-600 leading-relaxed">
                        {product.shortDescription}
                    </p>

                    {/* Variants */}
                    {product.variants?.map((v) => (
                        <div key={v.name} className="mt-6">
                            <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                                {v.name}
                                {variants[v.name] && (
                                    <span className="ml-2 text-zinc-900 normal-case font-medium">
                                        {variants[v.name]}
                                    </span>
                                )}
                            </p>
                            <div className="mt-2 flex flex-wrap gap-2">
                                {v.options.map((opt) => (
                                    <button
                                        key={opt}
                                        data-testid={`variant-${v.name}-${opt}`}
                                        onClick={() =>
                                            setVariants((s) => ({
                                                ...s,
                                                [v.name]: opt,
                                            }))
                                        }
                                        className={`px-3.5 py-1.5 text-sm rounded-md border transition-all ${
                                            variants[v.name] === opt
                                                ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                                                : "border-zinc-200 hover:border-zinc-300 text-zinc-700"
                                        }`}
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Quantity */}
                    <div className="mt-6 flex items-center gap-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
                            Cantidad
                        </p>
                        <div className="inline-flex items-center border border-zinc-200 rounded-md">
                            <button
                                data-testid="qty-decrement"
                                onClick={() => setQty((q) => Math.max(1, q - 1))}
                                className="w-9 h-9 grid place-items-center hover:bg-zinc-50"
                                aria-label="Disminuir"
                            >
                                <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-10 text-center text-sm font-medium">
                                {qty}
                            </span>
                            <button
                                data-testid="qty-increment"
                                onClick={() => setQty((q) => Math.min(product.stock, q + 1))}
                                className="w-9 h-9 grid place-items-center hover:bg-zinc-50"
                                aria-label="Aumentar"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                    </div>

                    {/* CTAs */}
                    <div className="mt-6 flex flex-col sm:flex-row gap-3">
                        <Button
                            data-testid="add-to-cart-button"
                            onClick={handleAddToCart}
                            size="lg"
                            className="flex-1 h-12 bg-zinc-900 hover:bg-zinc-800 text-white font-medium"
                        >
                            <ShoppingBag className="w-4 h-4 mr-2" />
                            Agregar al carrito
                        </Button>
                        <Button
                            data-testid="buy-now-button"
                            onClick={handleBuyNow}
                            size="lg"
                            className="flex-1 h-12 bg-primary hover:bg-primary-hover text-primary-foreground font-medium"
                        >
                            Comprar ahora
                        </Button>
                    </div>
                    <div className="mt-3 flex gap-2">
                        <Button variant="outline" className="flex-1 h-10 border-zinc-200 text-xs">
                            <Heart className="w-4 h-4 mr-1.5" /> Guardar
                        </Button>
                        <Button variant="outline" className="flex-1 h-10 border-zinc-200 text-xs">
                            <Share2 className="w-4 h-4 mr-1.5" /> Compartir
                        </Button>
                    </div>

                    {/* Store card */}
                    <Link
                        to={`/${store.slug}`}
                        data-testid="product-store-link"
                        className="mt-8 flex items-center gap-3 p-4 rounded-xl border border-zinc-200 hover:border-zinc-300 hover:shadow-sm transition-all"
                    >
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-zinc-100 shrink-0">
                            <img
                                src={store.logo}
                                alt={store.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold truncate">
                                {store.name}
                            </p>
                            <p className="text-xs text-zinc-500 flex items-center gap-1">
                                <MapPin className="w-3 h-3" />
                                {store.location?.city}, {store.location?.state}
                            </p>
                        </div>
                        <span className="text-xs font-medium text-indigo-600">
                            Ver tienda →
                        </span>
                    </Link>

                    {/* Shipping */}
                    <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-zinc-50 border border-zinc-200">
                        <Truck className="w-4 h-4 text-zinc-700 mt-0.5 shrink-0" />
                        <div>
                            <p className="text-xs font-semibold text-zinc-900">
                                Envío con Planeta Envíos
                            </p>
                            <p className="text-xs text-zinc-500 mt-0.5">
                                Estafeta · FedEx · DHL · J&T. Cotización al checkout.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="desc" className="mt-16">
                <TabsList data-testid="product-tabs" className="bg-transparent border-b border-zinc-200 rounded-none w-full justify-start h-auto p-0 gap-6">
                    <TabsTrigger
                        value="desc"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        Descripción
                    </TabsTrigger>
                    <TabsTrigger
                        value="ship"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        Envío
                    </TabsTrigger>
                    <TabsTrigger
                        value="returns"
                        className="rounded-none border-b-2 border-transparent data-[state=active]:border-indigo-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-3 text-sm"
                    >
                        Devoluciones
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="desc" className="pt-6">
                    <p className="text-sm text-zinc-600 leading-relaxed max-w-3xl">
                        {product.description}
                    </p>
                </TabsContent>
                <TabsContent value="ship" className="pt-6">
                    <p className="text-sm text-zinc-600 leading-relaxed max-w-3xl">
                        Envíos a todo México con paqueterías Estafeta, FedEx,
                        DHL y J&T gestionadas por Planeta Envíos. La cotización
                        exacta se calcula al momento del checkout según tu
                        código postal. Tiempo estimado: 2 a 6 días hábiles.
                    </p>
                </TabsContent>
                <TabsContent value="returns" className="pt-6">
                    <p className="text-sm text-zinc-600 leading-relaxed max-w-3xl">
                        Aceptamos devoluciones dentro de los primeros 7 días
                        después de recibido el pedido, siempre que el producto
                        regrese en su estado original. La política específica
                        depende de cada tienda; consulta con {store.name} para
                        más detalles.
                    </p>
                </TabsContent>
            </Tabs>

            {/* Related */}
            {related.length > 0 && (
                <section className="mt-20">
                    <h3 className="text-2xl font-semibold tracking-tight text-zinc-900">
                        Más de {store.name}
                    </h3>
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                        {related.slice(0, 4).map((p, i) => (
                            <ProductCard key={p.id} product={p} index={i} />
                        ))}
                    </div>
                </section>
            )}
        </div>
    );
}
